import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

declare global {
  interface Window {
    google?: any;
    addAutoRescueNotification?: (notification: Partial<Notification>) => void;
    openSightingModal?: (id: string) => void;
    autorescueDemo?: (opts?: Partial<Notification>) => void;
  }
}

type Notification = {
  id: string;
  plate: string;
  user: string;
  reportedByName?: string;
  reportedByUsername?: string;
  timestamp: string;
  location: { lat: number; lng: number };
  image: string;
};

type PlateRoute = {
  plate: string;
  notifications: Notification[];
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
const GOOGLE_MAPS_ID = process.env.REACT_APP_GOOGLE_MAPS_ID ?? '';
const API_POLL_INTERVAL_MS = Number(process.env.REACT_APP_AUTORESCUE_POLL_INTERVAL_MS ?? '15000');

const center = { lat: -33.4489, lng: -70.6693 };

const createPlateImage = (plate: string) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="420" height="280" viewBox="0 0 420 280">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#1b2b2f"/>
        <stop offset="100%" stop-color="#364248"/>
      </linearGradient>
    </defs>
    <rect width="420" height="280" rx="28" fill="url(#g)"/>
    <rect x="28" y="40" width="364" height="200" rx="18" fill="#f0f5f7"/>
    <text x="210" y="160" text-anchor="middle" font-family="Trebuchet MS, Arial" font-size="52" fill="#1c2328" letter-spacing="6">${plate}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const loadGoogleMapsScript = (key: string) =>
  new Promise<void>((resolve, reject) => {
    if (window.google?.maps) {
      resolve();
      return;
    }
    if (!key) {
      reject(new Error('Missing Google Maps API key.'));
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-maps="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps.')));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps.'));
    document.head.appendChild(script);
  });

const NIGHT_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1b2328' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1b2328' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9bb4c1' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2d3840' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0c1417' }] },
];

const formatTime = (timestamp: string) =>
  new Date(timestamp).toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
  });

const formatDateLabel = (timestamp: string) =>
  new Date(timestamp).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

const isSameDate = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const isWithinLastHours = (timestamp: string, hours: number) => {
  if (!Number.isFinite(hours) || hours <= 0) return true;
  const now = Date.now();
  const time = new Date(timestamp).getTime();
  if (Number.isNaN(time)) return false;
  return now - time <= hours * 60 * 60 * 1000;
};

const getReporterName = (notification: Notification) => notification.reportedByName ?? notification.user;

const buildInfoContent = (notification: Notification) => {
  const container = document.createElement('div');
  container.className = 'map-info';

  const image = document.createElement('img');
  image.src = notification.image;
  image.alt = `Patente ${notification.plate}`;
  image.style.cursor = 'pointer';
  image.addEventListener('click', (event) => {
    event.stopPropagation();
    if (window.openSightingModal) {
      window.openSightingModal(notification.id);
    }
  });

  const content = document.createElement('div');

  const header = document.createElement('div');
  header.className = 'map-info-header';

  const plate = document.createElement('strong');
  plate.textContent = notification.plate;

  const time = document.createElement('span');
  time.textContent = `${formatTime(notification.timestamp)} · ${formatDateLabel(notification.timestamp)}`;

  header.append(plate, time);

  const reporter = document.createElement('p');
  reporter.textContent = `Detectado por ${getReporterName(notification)}`;

  const location = document.createElement('p');
  location.textContent = `${notification.location.lat.toFixed(4)}, ${notification.location.lng.toFixed(4)}`;

  content.append(header, reporter, location);
  container.append(image, content);

  return container;
};

const groupByPlate = (notifications: Notification[]): PlateRoute[] => {
  const grouped = notifications.reduce<Record<string, Notification[]>>((acc, notification) => {
    acc[notification.plate] = acc[notification.plate] ?? [];
    acc[notification.plate].push(notification);
    return acc;
  }, {});
  return Object.entries(grouped).map(([plate, items]) => ({
    plate,
    notifications: items.sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
  }));
};

const getProjectedPath = (points: { lat: number; lng: number }[]) => {
  if (points.length < 2) return [];
  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  const vector = { lat: last.lat - prev.lat, lng: last.lng - prev.lng };
  return [
    { lat: last.lat + vector.lat * 0.35, lng: last.lng + vector.lng * 0.35 },
    { lat: last.lat + vector.lat * 0.7, lng: last.lng + vector.lng * 0.7 },
  ];
};

type ApiSighting = {
  id: number | string;
  plate: string;
  latitude: number | string;
  longitude: number | string;
  found_at?: string;
  image?: string | null;
  reported_by_name?: string | null;
  reported_by_username?: string | null;
  created_at?: string;
};

const normalizeBaseUrl = (value: string) => value.replace(/\/+$/, '');

const resolveApiUrl = (baseUrl: string, path: string) => {
  if (!baseUrl) return '';
  const base = normalizeBaseUrl(baseUrl);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

const mapSightingToNotification = (sighting: ApiSighting): Notification => ({
  id: `sighting-${sighting.id}`,
  plate: sighting.plate,
  user: sighting.reported_by_name || sighting.reported_by_username || 'Sistema',
  reportedByName: sighting.reported_by_name ?? undefined,
  reportedByUsername: sighting.reported_by_username ?? undefined,
  timestamp: sighting.found_at || sighting.created_at || new Date().toISOString(),
  location: {
    lat: Number(sighting.latitude),
    lng: Number(sighting.longitude),
  },
  image: sighting.image ?? '',
});

const getAuthToken = () =>
  process.env.REACT_APP_AUTORESCUE_AUTH_TOKEN ??
  window.localStorage.getItem('autorescue-auth-token') ??
  window.localStorage.getItem('authToken') ??
  '';

const mergeNotifications = (existing: Notification[], incoming: Notification[]) => {
  const merged = new Map(existing.map((item) => [item.id, item]));
  incoming.forEach((item) => merged.set(item.id, item));
  return Array.from(merged.values()).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
};

// ─── icons ──────────────────────────────────────────────────────────
const Icon = {
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  ),
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),
  clipboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="3" width="8" height="4" rx="1" />
      <path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
      <path d="M9 12h6M9 16h4" />
    </svg>
  ),
  doc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6M8 13h8M8 17h5" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M3 20c0-3 2.7-5 6-5s6 2 6 5M15 20c0-2.6 1.7-4 4-4s4 1.4 4 4" />
    </svg>
  ),
  car: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14M3 17v-4l2-5a2 2 0 0 1 2-1h10a2 2 0 0 1 2 1l2 5v4" />
      <circle cx="7" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
    </svg>
  ),
  box: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 7 9-4 9 4-9 4-9-4Z" />
      <path d="M3 7v10l9 4 9-4V7M12 11v10" />
    </svg>
  ),
  bell: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9Z" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </svg>
  ),
  video: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <path d="m22 8-6 4 6 4V8Z" />
    </svg>
  ),
  pin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s7-7 7-13a7 7 0 0 0-14 0c0 6 7 13 7 13Z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  ),
  history: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5M12 8v4l3 2" />
    </svg>
  ),
  cog: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4a2 2 0 0 1 2-2h7v18H4a2 2 0 0 1-2-2V4ZM13 2h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-7V2Z" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 18 6-6-6-6" />
    </svg>
  ),
  chevronDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  bars: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  mapPin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s7-7 7-13a7 7 0 0 0-14 0c0 6 7 13 7 13Z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  ),
  more: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  ),
  collapse: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16M14 9l-2 3 2 3" />
    </svg>
  ),
  fist: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 4 5v6c0 5 3.4 9 8 10 4.6-1 8-5 8-10V5l-8-3Z" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.3 3.9 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v5M12 18v.01" />
    </svg>
  ),
  knock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
    </svg>
  ),
  fight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12 9 8l4 4-4 4-4-4ZM15 6l3 3-3 3M11 21l4-4 4 4" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
    </svg>
  ),
  carSm: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14M3 17v-4l2-5a2 2 0 0 1 2-1h10a2 2 0 0 1 2 1l2 5v4" />
      <circle cx="7" cy="17" r="1.5" />
      <circle cx="17" cy="17" r="1.5" />
    </svg>
  ),
  backpack: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 21h10a2 2 0 0 0 2-2V9a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v10a2 2 0 0 0 2 2Z" />
      <path d="M9 5V3h6v2M8 12h8" />
    </svg>
  ),
};

// ─── dummy data ─────────────────────────────────────────────────────
const SAMPLE_INCIDENTS = [
  { id: 'INC-2024-00528', type: 'Robo a transeúnte', cat: 'red', icon: 'warning', loc: 'Av. Reforma, Col. Centro', date: '20/05/2024 11:30', status: 'process', statusLabel: 'En proceso', op: 'Operador 1' },
  { id: 'INC-2024-00527', type: 'Accidente vial', cat: 'orange', icon: 'carSm', loc: 'Blvd. Hidalgo y Juárez', date: '20/05/2024 10:58', status: 'done', statusLabel: 'Atendido', op: 'Operador 2' },
  { id: 'INC-2024-00526', type: 'Disturbio', cat: 'purple', icon: 'fight', loc: 'Parque Central', date: '20/05/2024 10:15', status: 'process', statusLabel: 'En proceso', op: 'Operador 3' },
  { id: 'INC-2024-00525', type: 'Allanamiento', cat: 'yellow', icon: 'knock', loc: 'Calle 5 de Mayo #120', date: '20/05/2024 09:42', status: 'pending', statusLabel: 'Pendiente', op: 'Operador 1' },
  { id: 'INC-2024-00524', type: 'Riña', cat: 'cyan', icon: 'fist', loc: 'Plaza del Sol', date: '20/05/2024 09:10', status: 'done', statusLabel: 'Atendido', op: 'Operador 2' },
];

const SAMPLE_REGISTROS = [
  { type: 'Persona', icon: 'user', cat: 'green', desc: 'Masculino, 28 años, sudadera negra', loc: 'Av. Juárez y Reforma', date: '20/05/2024 11:20', op: 'Operador 1' },
  { type: 'Vehículo', icon: 'carSm', cat: 'yellow', desc: 'Nissan Versa, Blanco, 2020', loc: 'Blvd. Hidalgo', date: '20/05/2024 11:05', op: 'Operador 2' },
  { type: 'Objeto', icon: 'backpack', cat: 'purple', desc: 'Mochila negra con logo', loc: 'Parque Central', date: '20/05/2024 10:50', op: 'Operador 3' },
];

const SAMPLE_DONUT = [
  { label: 'Robo', value: 109, pct: 35, color: '#ef4444' },
  { label: 'Violencia', value: 78, pct: 25, color: '#a855f7' },
  { label: 'Accidentes', value: 62, pct: 20, color: '#f59e0b' },
  { label: 'Daños', value: 31, pct: 10, color: '#06b6d4' },
  { label: 'Otros', value: 32, pct: 10, color: '#8794ab' },
];

const SAMPLE_LINE = [
  { x: '14 May', y: 52 },
  { x: '15 May', y: 64 },
  { x: '16 May', y: 58 },
  { x: '17 May', y: 70 },
  { x: '18 May', y: 80 },
  { x: '19 May', y: 60 },
  { x: '20 May', y: 68 },
];

const SAMPLE_CAMERAS = [
  { id: 'CAM-001', name: 'Av. Reforma y Juárez', tint: ['#1e3a8a', '#3b82f6'] },
  { id: 'CAM-002', name: 'Parque Central', tint: ['#14532d', '#22c55e'] },
  { id: 'CAM-003', name: 'Blvd. Hidalgo', tint: ['#3f1d72', '#a855f7'] },
  { id: 'CAM-004', name: 'Calle 5 de Mayo', tint: ['#7c2d12', '#f97316'] },
];

const cameraPlaceholder = (idx: number, name: string, tint: string[]) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 200'>
    <defs>
      <linearGradient id='g${idx}' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='${tint[0]}'/>
        <stop offset='100%' stop-color='${tint[1]}'/>
      </linearGradient>
    </defs>
    <rect width='320' height='200' fill='url(#g${idx})'/>
    <g fill='rgba(255,255,255,0.10)'>
      <rect x='20' y='110' width='80' height='90'/>
      <rect x='110' y='80' width='100' height='120'/>
      <rect x='220' y='100' width='80' height='100'/>
    </g>
    <g stroke='rgba(255,255,255,0.18)' stroke-width='1.5' fill='none'>
      <line x1='0' y1='160' x2='320' y2='160'/>
      <line x1='0' y1='175' x2='320' y2='175'/>
    </g>
    <circle cx='${60 + (idx * 30)}' cy='168' r='4' fill='#fff' opacity='0.8'/>
    <circle cx='${170 + (idx * 25)}' cy='172' r='3' fill='#fff' opacity='0.6'/>
    <text x='160' y='105' text-anchor='middle' font-family='Inter,sans-serif' font-size='13' font-weight='600' fill='rgba(255,255,255,0.4)' letter-spacing='1'>${name.toUpperCase()}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

// ─── chart components ───────────────────────────────────────────────
const DonutChart: React.FC<{ data: typeof SAMPLE_DONUT; total: number }> = ({ data, total }) => {
  const size = 160;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="donut-wrap">
      <svg className="donut-svg" viewBox={`0 0 ${size} ${size}`}>
        <g transform={`translate(${size / 2} ${size / 2}) rotate(-90)`}>
          <circle r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} />
          {data.map((d) => {
            const len = (d.pct / 100) * c;
            const dash = `${len} ${c}`;
            const dashoffset = -offset;
            offset += len;
            return (
              <circle
                key={d.label}
                r={r}
                fill="none"
                stroke={d.color}
                strokeWidth={stroke}
                strokeDasharray={dash}
                strokeDashoffset={dashoffset}
                strokeLinecap="butt"
              />
            );
          })}
        </g>
        <g className="donut-center">
          <text x={size / 2} y={size / 2 - 6} className="donut-center-label">Total</text>
          <text x={size / 2} y={size / 2 + 18} className="donut-center-value">{total}</text>
        </g>
      </svg>
      <div className="donut-legend">
        {data.map((d) => (
          <div key={d.label} className="donut-legend-item">
            <span className="dot" style={{ background: d.color }} />
            <span className="label">{d.label}</span>
            <span className="value">{d.pct}% ({d.value})</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LineChart: React.FC<{ data: typeof SAMPLE_LINE }> = ({ data }) => {
  const w = 520;
  const h = 200;
  const padL = 36;
  const padR = 12;
  const padT = 14;
  const padB = 28;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const maxY = 100;
  const stepX = innerW / (data.length - 1);
  const points = data.map((d, i) => ({
    x: padL + i * stepX,
    y: padT + innerH * (1 - d.y / maxY),
  }));
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
  const area = `${path} L ${padL + innerW} ${padT + innerH} L ${padL} ${padT + innerH} Z`;
  return (
    <div className="linechart-wrap">
      <svg className="linechart-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        {/* Y axis labels + grid */}
        {[0, 20, 40, 60, 80, 100].map((v) => {
          const y = padT + innerH * (1 - v / maxY);
          return (
            <g key={v}>
              <line x1={padL} y1={y} x2={padL + innerW} y2={y} stroke="rgba(255,255,255,0.04)" />
              <text x={padL - 8} y={y + 3} textAnchor="end" className="linechart-axis-label">{v}</text>
            </g>
          );
        })}
        <defs>
          <linearGradient id="line-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#line-area)" />
        <path d={path} stroke="#3b82f6" strokeWidth="2.2" fill="none" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#3b82f6" stroke="#0a1124" strokeWidth="2" />
        ))}
        {data.map((d, i) => (
          <text
            key={d.x}
            x={padL + i * stepX}
            y={h - 8}
            textAnchor="middle"
            className="linechart-axis-label"
          >
            {d.x}
          </text>
        ))}
      </svg>
    </div>
  );
};

function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedPlate, setSelectedPlate] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<'all' | 'today' | '24h' | '7d'>('24h');
  const apiBaseUrl =
    typeof window !== 'undefined' && typeof (window as any).__AUTORESCUE_API_BASE_URL__ === 'string'
      ? (window as any).__AUTORESCUE_API_BASE_URL__
      : process.env.REACT_APP_AUTORESCUE_API_BASE_URL ?? '';
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const routeRendererRef = useRef<any | null>(null);
  const projectedRouteRendererRef = useRef<any | null>(null);
  const infoWindowRef = useRef<any | null>(null);
  const pendingFocusRef = useRef<Notification | null>(null);
  const highlightIdRef = useRef<string | null>(null);
  const [mapAlertPulse, setMapAlertPulse] = useState(false);
  const directionsRequestRef = useRef(0);
  const fetchInFlightRef = useRef(false);
  const knownIdsRef = useRef<Set<string>>(new Set());
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [lastFetchAt, setLastFetchAt] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [authToken, setAuthToken] = useState<string>(() => getAuthToken());
  const [authEmail, setAuthEmail] = useState<string>('');
  const [authPassword, setAuthPassword] = useState<string>('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSightingModal, setShowSightingModal] = useState(false);
  const [activeSighting, setActiveSighting] = useState<Notification | null>(null);
  const [showImageFullscreen, setShowImageFullscreen] = useState(false);

  const normalizeNotification = (notification: Notification): Notification => ({
    ...notification,
    image: notification.image || createPlateImage(notification.plate),
  });

  const flagNewNotifications = (incoming: Notification[]) => {
    if (incoming.length === 0) return;
    const newest = incoming[0];
    setSelectedPlate(newest.plate);
    highlightIdRef.current = newest.id;
    pendingFocusRef.current = newest;
    setFreshNotificationIds((prev) => {
      const next = new Set(prev);
      incoming.forEach((item) => next.add(item.id));
      return next;
    });
    setMapAlertPulse(true);
    incoming.forEach((item) => {
      window.setTimeout(() => {
        setFreshNotificationIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }, 4000);
    });
    window.setTimeout(() => {
      setMapAlertPulse(false);
    }, 2200);
  };

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [freshNotificationIds, setFreshNotificationIds] = useState<Set<string>>(new Set());

  const clearAuthToken = () => {
    window.localStorage.removeItem('autorescue-auth-token');
    window.localStorage.removeItem('authToken');
    setAuthToken('');
    setAuthEmail('');
    setAuthPassword('');
    setAuthError(null);
    setFetchError(null);
    setLastFetchAt(null);
  };

  const handleLogin = async () => {
    if (!apiBaseUrl) {
      setAuthError('Configura la URL del API antes de iniciar sesion.');
      return;
    }
    if (!authEmail.trim() || !authPassword.trim()) {
      setAuthError('Ingresa tu email y contrasena.');
      return;
    }
    const url = resolveApiUrl(apiBaseUrl, '/api/auth/login/');
    if (!url) return;
    setAuthLoading(true);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: authEmail.trim(), password: authPassword }),
      });
      if (!response.ok) {
        throw new Error('Credenciales invalidas.');
      }
      const data = await response.json();
      const token = typeof data?.token === 'string' ? data.token : '';
      if (!token) {
        throw new Error('Respuesta de autenticacion invalida.');
      }
      window.localStorage.setItem('autorescue-auth-token', token);
      setAuthToken(token);
      setAuthError(null);
      setFetchError(null);
      setShowAuthModal(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo iniciar sesion.';
      setAuthError(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const filteredNotifications = useMemo(() => {
    const now = new Date();
    if (timeFilter === 'all') return notifications;
    if (timeFilter === 'today') {
      return notifications.filter((notification) => isSameDate(new Date(notification.timestamp), now));
    }
    if (timeFilter === '24h') {
      return notifications.filter((notification) => isWithinLastHours(notification.timestamp, 24));
    }
    if (timeFilter === '7d') {
      return notifications.filter((notification) => isWithinLastHours(notification.timestamp, 24 * 7));
    }
    return notifications;
  }, [notifications, timeFilter]);

  const routes = useMemo(() => groupByPlate(filteredNotifications), [filteredNotifications]);
  const plateOptions = useMemo(() => routes.map((route) => route.plate), [routes]);
  const selectedRoute = useMemo(
    () => routes.find((route) => route.plate === selectedPlate),
    [routes, selectedPlate]
  );

  const visibleNotifications = useMemo(
    () =>
      authToken
        ? selectedPlate === 'all'
          ? filteredNotifications
          : filteredNotifications.filter((notification) => notification.plate === selectedPlate)
        : [],
    [authToken, filteredNotifications, selectedPlate]
  );

  useEffect(() => {
    if (selectedPlate !== 'all' && !plateOptions.includes(selectedPlate)) {
      setSelectedPlate('all');
    }
  }, [plateOptions, selectedPlate]);

  useEffect(() => {
    document.body.classList.remove('theme-day');
    document.body.classList.add('theme-night');
  }, []);

  useEffect(() => {
    let active = true;
    if (!GOOGLE_MAPS_API_KEY) {
      setMapError(null);
      return () => {
        active = false;
      };
    }
    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (!active || !mapContainerRef.current) return;
        mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
          center,
          zoom: 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          mapId: GOOGLE_MAPS_ID || undefined,
          styles: GOOGLE_MAPS_ID ? undefined : NIGHT_MAP_STYLES,
          backgroundColor: '#06090c',
          colorScheme:
            window.google.maps.ColorScheme?.DARK ?? 'DARK',
        });
        infoWindowRef.current = new window.google.maps.InfoWindow();
        (window as any).__autorescueMap = mapRef.current;
        const logRendering = () => {
          // eslint-disable-next-line no-console
          console.log(
            '%c[AutoRescue]%c map · mapId=%s · rendering=%s',
            'color:#ffb547;font-weight:700',
            'color:#9fb0b8',
            GOOGLE_MAPS_ID || '(none)',
            typeof mapRef.current?.getRenderingType === 'function'
              ? mapRef.current.getRenderingType()
              : 'unknown'
          );
        };
        logRendering();
        if (typeof mapRef.current.addListener === 'function') {
          mapRef.current.addListener('renderingtype_changed', logRendering);
          mapRef.current.addListener('tilesloaded', logRendering);
        }
        setMapReady(true);
        setMapError(null);
      })
      .catch((error) => {
        if (!active) return;
        setMapError(error instanceof Error ? error.message : 'Failed to load Google Maps.');
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!apiBaseUrl || !authToken) return;
    const url = resolveApiUrl(apiBaseUrl, '/api/stolen-plates/sightings/');
    if (!url) return;
    let active = true;
    let pollTimer: number | null = null;

    const fetchSightings = async () => {
      if (!active || fetchInFlightRef.current) return;
      fetchInFlightRef.current = true;
      setIsFetching(true);
      try {
        const headers: HeadersInit = {
          Accept: 'application/json',
          Authorization: `Token ${authToken}`,
        };
        const response = await fetch(url, { headers });
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error('Autenticacion requerida');
          }
          throw new Error(`API error ${response.status}`);
        }
        const data = await response.json();
        const raw = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : null;
        if (!raw) {
          throw new Error('Unexpected API response.');
        }
        const mapped = raw
          .map((item: ApiSighting) => normalizeNotification(mapSightingToNotification(item)))
          .filter((item: Notification) => Number.isFinite(item.location.lat) && Number.isFinite(item.location.lng))
          .sort((a: Notification, b: Notification) => b.timestamp.localeCompare(a.timestamp));
        const newItems = mapped.filter((item: Notification) => !knownIdsRef.current.has(item.id));
        mapped.forEach((item: Notification) => knownIdsRef.current.add(item.id));
        setNotifications((prev) => mergeNotifications(prev, mapped));
        if (newItems.length > 0) {
          newItems.sort((a: Notification, b: Notification) => b.timestamp.localeCompare(a.timestamp));
          flagNewNotifications(newItems);
        }
        setAuthError(null);
        setFetchError(null);
        setLastFetchAt(new Date().toISOString());
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'No se pudieron cargar las notificaciones.';
        if (message.toLowerCase().includes('autenticacion')) {
          setAuthError('Token invalido o expirado.');
          setFetchError('Autenticacion requerida.');
        } else {
          setFetchError(message);
        }
      } finally {
        fetchInFlightRef.current = false;
        setIsFetching(false);
      }
    };

    fetchSightings();
    if (Number.isFinite(API_POLL_INTERVAL_MS) && API_POLL_INTERVAL_MS > 0) {
      pollTimer = window.setInterval(fetchSightings, API_POLL_INTERVAL_MS);
    }
    return () => {
      active = false;
      if (pollTimer) {
        window.clearInterval(pollTimer);
      }
    };
  }, [apiBaseUrl, authToken]);

  useEffect(() => {
    window.addAutoRescueNotification = (incoming: Partial<Notification>) => {
      if (!incoming.plate || !incoming.user || !incoming.location) return;
      const notification: Notification = normalizeNotification({
        id: incoming.id ?? `ntf-${Date.now()}`,
        plate: incoming.plate,
        user: incoming.user,
        reportedByName: incoming.reportedByName,
        reportedByUsername: incoming.reportedByUsername,
        timestamp: incoming.timestamp ?? new Date().toISOString(),
        location: incoming.location,
        image: incoming.image ?? '',
      });
      knownIdsRef.current.add(notification.id);
      setNotifications((prev) => mergeNotifications(prev, [notification]));
      flagNewNotifications([notification]);
    };
    return () => {
      delete window.addAutoRescueNotification;
    };
  }, []);

  useEffect(() => {
    const SANTIAGO_HOTSPOTS = [
      { name: 'Plaza Italia', lat: -33.4378, lng: -70.6344 },
      { name: 'Las Condes', lat: -33.4172, lng: -70.5938 },
      { name: 'Providencia', lat: -33.4255, lng: -70.6113 },
      { name: 'Bellavista', lat: -33.4317, lng: -70.6344 },
      { name: 'Centro / Plaza de Armas', lat: -33.4372, lng: -70.6506 },
      { name: 'Ñuñoa', lat: -33.4564, lng: -70.6092 },
    ];
    const randomPlate = () => {
      const letters = 'ABCDEFGHJKLMPRSTVWXYZ';
      const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
      return `${pick(letters)}${pick(letters)}${pick(letters)}${pick(letters)}-${Math.floor(
        Math.random() * 90 + 10
      )}`;
    };
    const reporters = ['Camila Rojas', 'Matías Ponce', 'Ignacio Soto', 'Patrulla Móvil 04', 'Drone Aéreo 12'];

    window.autorescueDemo = (opts) => {
      if (!authToken) {
        const token = 'demo-mode';
        window.localStorage.setItem('autorescue-auth-token', token);
        setAuthToken(token);
      }
      const spot = SANTIAGO_HOTSPOTS[Math.floor(Math.random() * SANTIAGO_HOTSPOTS.length)];
      const jitter = () => (Math.random() - 0.5) * 0.004;
      const location = opts?.location ?? { lat: spot.lat + jitter(), lng: spot.lng + jitter() };
      const plate = opts?.plate ?? randomPlate();
      const reporter = opts?.reportedByName ?? reporters[Math.floor(Math.random() * reporters.length)];
      // delay one tick so auth-token-driven effects mount addAutoRescueNotification
      window.setTimeout(() => {
        window.addAutoRescueNotification?.({
          id: opts?.id ?? `demo-${Date.now()}`,
          plate,
          user: reporter,
          reportedByName: reporter,
          timestamp: opts?.timestamp ?? new Date().toISOString(),
          location,
          image: opts?.image ?? '',
        });
        // eslint-disable-next-line no-console
        console.log(
          `%c[AutoRescue]%c detección demo · ${plate} @ ${spot.name} (${location.lat.toFixed(
            4
          )}, ${location.lng.toFixed(4)})`,
          'color:#ffb547;font-weight:700',
          'color:#9fb0b8'
        );
      }, 50);
    };

    // eslint-disable-next-line no-console
    console.log(
      '%c[AutoRescue]%c modo demo activo — ejecuta %cwindow.autorescueDemo()%c en la consola para simular una detección.',
      'color:#ffb547;font-weight:700',
      'color:#9fb0b8',
      'color:#4dd0c2;font-weight:700',
      'color:#9fb0b8'
    );

    return () => {
      delete window.autorescueDemo;
    };
  }, [authToken]);

  useEffect(() => {
    window.openSightingModal = (id: string) => {
      const found = notifications.find((item) => item.id === id);
      if (!found) return;
      setActiveSighting(found);
      setShowSightingModal(true);
    };
    return () => {
      delete window.openSightingModal;
    };
  }, [notifications]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
    if (routeRendererRef.current) {
      routeRendererRef.current.setMap(null);
      routeRendererRef.current = null;
    }
    if (projectedRouteRendererRef.current) {
      projectedRouteRendererRef.current.setMap(null);
      projectedRouteRendererRef.current = null;
    }

    visibleNotifications.forEach((notification) => {
      const marker = new window.google.maps.Marker({
        position: notification.location,
        map: mapRef.current,
        title: `${notification.plate} · ${getReporterName(notification)}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: '#f6c453',
          fillOpacity: 0.95,
          strokeColor: '#1b2328',
          strokeWeight: 2,
          scale: 7,
        },
      });
      if (notification.id === highlightIdRef.current) {
        marker.setAnimation(window.google.maps.Animation.BOUNCE);
        window.setTimeout(() => marker.setAnimation(null), 1800);
        highlightIdRef.current = null;
      }
      marker.addListener('click', () => {
        if (!infoWindowRef.current) return;
        infoWindowRef.current.setContent(buildInfoContent(notification));
        infoWindowRef.current.open(mapRef.current, marker);
      });
      markersRef.current.push(marker);
    });

    if (pendingFocusRef.current) {
      const notification = pendingFocusRef.current;
      const markerMatch = markersRef.current.find(
        (marker) =>
          marker.getPosition()?.lat() === notification.location.lat &&
          marker.getPosition()?.lng() === notification.location.lng
      );
      infoWindowRef.current?.setContent(buildInfoContent(notification));
      if (markerMatch) {
        infoWindowRef.current?.open(mapRef.current, markerMatch);
      } else {
        infoWindowRef.current?.setPosition(notification.location);
        infoWindowRef.current?.open(mapRef.current);
      }
      flyToDetection(notification.location);
      setStreetViewTarget(notification);
      pendingFocusRef.current = null;
    }

    if (selectedRoute && selectedRoute.notifications.length > 1) {
      const path = selectedRoute.notifications.map((item) => item.location);
      const service = new window.google.maps.DirectionsService();
      const requestId = directionsRequestRef.current + 1;
      directionsRequestRef.current = requestId;

      const origin = path[0];
      const destination = path[path.length - 1];
      const waypoints = path.slice(1, -1).map((point) => ({
        location: point,
        stopover: false,
      }));

      service.route(
        {
          origin,
          destination,
          waypoints,
          travelMode: window.google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false,
        },
        (result: any, status: string) => {
          if (directionsRequestRef.current !== requestId) return;
          if (status !== 'OK' || !result) return;
          routeRendererRef.current = new window.google.maps.DirectionsRenderer({
            map: mapRef.current,
            directions: result,
            preserveViewport: true,
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: '#5bd6f4',
              strokeOpacity: 0.9,
              strokeWeight: 4,
            },
          });
          if (result.routes?.[0]?.bounds) {
            mapRef.current.fitBounds(result.routes[0].bounds);
          }
        }
      );

      const projected = getProjectedPath(path);
      const projectedTarget = projected[projected.length - 1];
      if (projectedTarget) {
        service.route(
          {
            origin: destination,
            destination: projectedTarget,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result: any, status: string) => {
            if (directionsRequestRef.current !== requestId) return;
            if (status !== 'OK' || !result) return;
            projectedRouteRendererRef.current = new window.google.maps.DirectionsRenderer({
              map: mapRef.current,
              directions: result,
              preserveViewport: true,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#ff8f66',
                strokeOpacity: 0.9,
                strokeWeight: 3,
                icons: [
                  {
                    icon: {
                      path: 'M 0,-1 0,1',
                      strokeOpacity: 0.9,
                      scale: 4,
                    },
                    offset: '0',
                    repeat: '12px',
                  },
                ],
              },
            });
          }
        );
      }
    }
  }, [mapReady, visibleNotifications, selectedRoute]);

  const streetViewContainerRef = useRef<HTMLDivElement | null>(null);
  const streetViewRef = useRef<any>(null);
  const flyAnimationRef = useRef<number | null>(null);
  const [streetViewTarget, setStreetViewTarget] = useState<Notification | null>(null);
  const [streetViewState, setStreetViewState] = useState<'loading' | 'ok' | 'unavailable'>('loading');

  const flyToDetection = (location: { lat: number; lng: number }) => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    if (flyAnimationRef.current !== null) {
      cancelAnimationFrame(flyAnimationRef.current);
      flyAnimationRef.current = null;
    }
    const startTilt = (typeof map.getTilt === 'function' ? map.getTilt() : 0) || 0;
    const startHeading = (typeof map.getHeading === 'function' ? map.getHeading() : 0) || 0;
    const startZoom = map.getZoom() ?? 13;
    const startCenter = map.getCenter();
    const fromLat = startCenter?.lat?.() ?? location.lat;
    const fromLng = startCenter?.lng?.() ?? location.lng;
    const targetTilt = 67.5;
    const targetZoom = 19;
    const targetHeading = (startHeading + 60 + Math.random() * 120) % 360;
    const duration = 1800;
    const t0 = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const setCam = (state: any) => {
      if (typeof map.moveCamera === 'function') {
        map.moveCamera(state);
      } else {
        map.setCenter(state.center);
        map.setZoom(state.zoom);
        if (typeof map.setTilt === 'function') map.setTilt(state.tilt);
        if (typeof map.setHeading === 'function') map.setHeading(state.heading);
      }
    };
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / duration);
      const k = ease(t);
      setCam({
        center: {
          lat: fromLat + (location.lat - fromLat) * k,
          lng: fromLng + (location.lng - fromLng) * k,
        },
        zoom: startZoom + (targetZoom - startZoom) * k,
        tilt: startTilt + (targetTilt - startTilt) * k,
        heading: startHeading + (targetHeading - startHeading) * k,
      });
      if (t < 1) {
        flyAnimationRef.current = requestAnimationFrame(step);
      } else {
        flyAnimationRef.current = null;
      }
    };
    flyAnimationRef.current = requestAnimationFrame(step);
  };

  // mount Street View when target changes
  useEffect(() => {
    if (!streetViewTarget || !streetViewContainerRef.current || !window.google?.maps) return;
    setStreetViewState('loading');
    const svService = new window.google.maps.StreetViewService();
    svService.getPanorama(
      { location: streetViewTarget.location, radius: 80, source: 'outdoor' },
      (data: any, status: string) => {
        if (status !== 'OK' || !data?.location?.latLng) {
          setStreetViewState('unavailable');
          return;
        }
        const bearing = window.google.maps.geometry?.spherical
          ? window.google.maps.geometry.spherical.computeHeading(
              data.location.latLng,
              new window.google.maps.LatLng(streetViewTarget.location)
            )
          : 0;
        if (!streetViewRef.current) {
          streetViewRef.current = new window.google.maps.StreetViewPanorama(
            streetViewContainerRef.current,
            {
              pano: data.location.pano,
              pov: { heading: bearing, pitch: 0 },
              zoom: 0.6,
              addressControl: false,
              fullscreenControl: false,
              motionTracking: false,
              motionTrackingControl: false,
              showRoadLabels: false,
              linksControl: false,
              panControl: false,
              zoomControl: false,
            }
          );
        } else {
          streetViewRef.current.setPano(data.location.pano);
          streetViewRef.current.setPov({ heading: bearing, pitch: 0 });
          streetViewRef.current.setVisible(true);
        }
        setStreetViewState('ok');
      }
    );
  }, [streetViewTarget]);

  const closeStreetView = () => {
    setStreetViewTarget(null);
    if (streetViewRef.current && typeof streetViewRef.current.setVisible === 'function') {
      streetViewRef.current.setVisible(false);
    }
    // restore overhead
    const map = mapRef.current;
    if (map) {
      if (typeof map.setTilt === 'function') map.setTilt(0);
      if (typeof map.setHeading === 'function') map.setHeading(0);
      if (typeof map.setZoom === 'function') map.setZoom(14);
    }
  };

  const [clock, setClock] = useState<string>(() =>
    new Date().toLocaleTimeString('es-CL', { hour12: false })
  );
  useEffect(() => {
    const id = window.setInterval(
      () => setClock(new Date().toLocaleTimeString('es-CL', { hour12: false })),
      1000
    );
    return () => window.clearInterval(id);
  }, []);

  const activePlates = useMemo(
    () => new Set(notifications.map((n) => n.plate)).size,
    [notifications]
  );

  const handleNotificationClick = (notification: Notification) => {
    if (!mapRef.current || !infoWindowRef.current || !mapReady) return;
    pendingFocusRef.current = notification;
    if (selectedPlate !== notification.plate) {
      setSelectedPlate(notification.plate);
    }
    flyToDetection(notification.location);
    setStreetViewTarget(notification);
    if (selectedPlate === notification.plate) {
      infoWindowRef.current.setContent(buildInfoContent(notification));
      infoWindowRef.current.setPosition(notification.location);
      infoWindowRef.current.open(mapRef.current);
      pendingFocusRef.current = null;
    }
  };

  const todayDateStr = new Date().toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const detectionsToday = useMemo(
    () => notifications.filter((n) => isSameDate(new Date(n.timestamp), new Date())).length,
    [notifications]
  );

  return (
    <div className={`App ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-shield">{Icon.shield}</div>
          <div className="brand-info">
            <span className="brand-info-title">AUTORESCUE</span>
            <span className="brand-info-sub">Centro de Control</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <span className="nav-section-title">Menú Principal</span>
          <button className="nav-item active">{Icon.dashboard}<span className="nav-item-label">Dashboard</span></button>
          <button className="nav-item">{Icon.clipboard}<span className="nav-item-label">Incidentes</span></button>
          <button className="nav-item">{Icon.doc}<span className="nav-item-label">Reportes</span></button>
          <button className="nav-item">{Icon.users}<span className="nav-item-label">Personas</span></button>
          <button className="nav-item">{Icon.car}<span className="nav-item-label">Vehículos</span></button>
          <button className="nav-item">{Icon.box}<span className="nav-item-label">Objetos</span></button>
          <button className="nav-item">{Icon.bell}<span className="nav-item-label">Alertas</span></button>
          <button className="nav-item">
            {Icon.video}
            <span className="nav-item-label">CCTV</span>
            <span className="nav-item-chev">{Icon.chevronDown}</span>
          </button>
          <button className="nav-item nav-item-sub"><span className="nav-item-label">Mapa de Cámaras</span></button>
          <button className="nav-item nav-item-sub"><span className="nav-item-label">Monitoreo en Vivo</span></button>
          <span className="nav-section-title">Análisis</span>
          <button className="nav-item">{Icon.chart}<span className="nav-item-label">Estadísticas</span></button>
          <button className="nav-item">{Icon.history}<span className="nav-item-label">Historial</span></button>
          <span className="nav-section-title">Configuración</span>
          <button className="nav-item">{Icon.users}<span className="nav-item-label">Usuarios</span></button>
          <button className="nav-item">{Icon.fist}<span className="nav-item-label">Roles</span></button>
          <button className="nav-item">{Icon.cog}<span className="nav-item-label">Configuración</span></button>
          <button className="nav-item">{Icon.book}<span className="nav-item-label">Bitácora</span></button>
        </nav>
        <div className="sidebar-collapse">
          <button
            type="button"
            className="nav-item"
            onClick={() => setSidebarCollapsed((v) => !v)}
            aria-label={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {Icon.collapse}
            <span className="sidebar-collapse-label nav-item-label">
              {sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            </span>
          </button>
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed((v) => !v)}
              aria-label="Alternar menú"
            >
              {Icon.bars}
            </button>
          </div>
          <div className="topbar-right">
            <button type="button" className="topbar-icon-btn" aria-label="Notificaciones">
              {Icon.bell}
              <span className="notif-badge">{Math.max(detectionsToday, 12)}</span>
            </button>
            {!authToken && (
              <button
                type="button"
                className="topbar-icon-btn"
                onClick={() => setShowAuthModal(true)}
                style={{ width: 'auto', padding: '0 14px', fontSize: '0.82rem', fontWeight: 600 }}
              >
                Iniciar sesión
              </button>
            )}
            {authToken && (
              <button
                type="button"
                className="topbar-icon-btn"
                onClick={clearAuthToken}
                style={{ width: 'auto', padding: '0 14px', fontSize: '0.82rem', fontWeight: 600 }}
              >
                Cerrar sesión
              </button>
            )}
            <div className="user-pill">
              <div className="user-pill-text">
                <span className="user-pill-name">Operador 1</span>
                <span className="user-pill-role">Administrador</span>
              </div>
              <div className="user-avatar">O1</div>
            </div>
          </div>
        </header>

        <div className="page-header">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">AutoRescue centro de monitoreo — Bienvenido al centro de control</p>
          </div>
          <div className="page-meta">
            <span className="meta-pill">{Icon.calendar} {todayDateStr}</span>
            <span className="meta-pill">{Icon.clock} {clock}</span>
          </div>
        </div>

        <div className="dash-content">
          {/* ========== KPI row ========== */}
          <section className="kpi-row" aria-label="Métricas">
            <article className="kpi-card">
              <div className="kpi-icon orange">{Icon.clipboard}</div>
              <div className="kpi-body">
                <span className="kpi-label">Detecciones (hoy)</span>
                <span className="kpi-value">{detectionsToday || 28}</span>
                <span className="kpi-delta up">↑ 12% vs ayer</span>
              </div>
            </article>
            <article className="kpi-card">
              <div className="kpi-icon red">{Icon.warning}</div>
              <div className="kpi-body">
                <span className="kpi-label">Alertas activas</span>
                <span className="kpi-value">{freshNotificationIds.size || 7}</span>
                <span className="kpi-delta down">↑ 16% vs ayer</span>
              </div>
            </article>
            <article className="kpi-card">
              <div className="kpi-icon green">{Icon.users}</div>
              <div className="kpi-body">
                <span className="kpi-label">Placas únicas</span>
                <span className="kpi-value">{activePlates ? activePlates.toLocaleString() : '1,245'}</span>
                <span className="kpi-delta up">↑ 8% vs mes anterior</span>
              </div>
            </article>
            <article className="kpi-card">
              <div className="kpi-icon yellow">{Icon.car}</div>
              <div className="kpi-body">
                <span className="kpi-label">Vehículos rastreados</span>
                <span className="kpi-value">892</span>
                <span className="kpi-delta up">↑ 5% vs mes anterior</span>
              </div>
            </article>
            <article className="kpi-card">
              <div className="kpi-icon purple">{Icon.video}</div>
              <div className="kpi-body">
                <span className="kpi-label">Cámaras activas</span>
                <span className="kpi-value">42 / 58</span>
                <span className="kpi-delta">72% del total</span>
              </div>
            </article>
          </section>

          {/* ========== dashboard grid ========== */}
          <div className="dashboard-grid">
            {/* ─── LEFT column ─── */}
            <div className="col-stack">
              {/* incidents table */}
              <section className="panel">
                <div className="panel-header">
                  <h2 className="panel-title">Incidentes recientes</h2>
                </div>
                <div className="panel-body">
                  <table className="incidents-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Tipo</th>
                        <th>Ubicación</th>
                        <th>Fecha/Hora</th>
                        <th>Estado</th>
                        <th>Registrado por</th>
                        <th aria-label="acciones" />
                      </tr>
                    </thead>
                    <tbody>
                      {SAMPLE_INCIDENTS.map((row) => (
                        <tr key={row.id}>
                          <td className="incident-id">{row.id}</td>
                          <td>
                            <span className="incident-type-cell">
                              <span className={`incident-type-icon ${row.cat}`}>
                                {(Icon as any)[row.icon]}
                              </span>
                              {row.type}
                            </span>
                          </td>
                          <td>
                            <span className="incident-location">
                              {Icon.mapPin} {row.loc}
                            </span>
                          </td>
                          <td>{row.date}</td>
                          <td>
                            <span className={`status-chip ${row.status}`}>{row.statusLabel}</span>
                          </td>
                          <td>{row.op}</td>
                          <td>
                            <button className="table-action" aria-label="Más">{Icon.more}</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="panel-footer">
                  <button className="panel-link">Ver todos los incidentes →</button>
                </div>
              </section>

              {/* charts row */}
              <div className="charts-row">
                <section className="panel">
                  <div className="panel-header">
                    <h2 className="panel-title">Incidentes por tipo (30 días)</h2>
                  </div>
                  <DonutChart
                    data={SAMPLE_DONUT}
                    total={SAMPLE_DONUT.reduce((s, d) => s + d.value, 0)}
                  />
                </section>
                <section className="panel">
                  <div className="panel-header">
                    <h2 className="panel-title">Incidentes por día</h2>
                    <select className="range-select" defaultValue="7">
                      <option value="7">Últimos 7 días</option>
                      <option value="14">Últimos 14 días</option>
                      <option value="30">Últimos 30 días</option>
                    </select>
                  </div>
                  <LineChart data={SAMPLE_LINE} />
                </section>
              </div>

              {/* registros */}
              <section className="panel">
                <div className="panel-header">
                  <h2 className="panel-title">Registros recientes</h2>
                </div>
                <div className="panel-body">
                  <table className="registros-table">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Descripción</th>
                        <th>Ubicación</th>
                        <th>Fecha/Hora</th>
                        <th>Registrado por</th>
                        <th aria-label="acciones" />
                      </tr>
                    </thead>
                    <tbody>
                      {SAMPLE_REGISTROS.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            <span className="registro-type">
                              <span className={`registro-type-icon ${row.cat}`}>
                                {(Icon as any)[row.icon]}
                              </span>
                              {row.type}
                            </span>
                          </td>
                          <td>{row.desc}</td>
                          <td>{row.loc}</td>
                          <td>{row.date}</td>
                          <td>{row.op}</td>
                          <td>
                            <button className="table-action" aria-label="Más">{Icon.more}</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="panel-footer">
                  <button className="panel-link">Ver todos los registros →</button>
                </div>
              </section>
            </div>

            {/* ─── RIGHT column ─── */}
            <div className="col-stack">
              {/* cameras */}
              <section className="panel">
                <div className="panel-header">
                  <h2 className="panel-title">Cámaras en vivo</h2>
                  <button className="panel-link">Ver todas →</button>
                </div>
                <div className="cameras-grid">
                  {SAMPLE_CAMERAS.map((c, idx) => (
                    <div key={c.id} className="camera-tile">
                      <img src={cameraPlaceholder(idx, c.name, c.tint)} alt={c.name} />
                      <div className="camera-tile-header">
                        <span className="camera-label">
                          <span className="id">{c.id}</span>{c.name}
                        </span>
                        <span className="camera-live">
                          <span className="dot" />EN VIVO
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="panel-footer">
                  <button className="panel-link">Ver todas las cámaras →</button>
                </div>
              </section>

              {/* map widget */}
              <section className="panel map-widget">
                <div className="panel-header">
                  <h2 className="panel-title">
                    Mapa de detecciones <span style={{ opacity: 0.55, fontWeight: 500 }}>(en tiempo real)</span>
                  </h2>
                  <button className="panel-link">Ver mapa completo →</button>
                </div>
                <div className={`map-frame ${mapAlertPulse ? 'alert' : ''}`}>
                  <div className="map-canvas" ref={mapContainerRef} />
                  {streetViewTarget && (
                    <div className="streetview-panel" role="dialog" aria-label="Vista a nivel de calle">
                      <div className="streetview-header">
                        <span className="streetview-title">
                          <span className="dot" /> STREET VIEW · {streetViewTarget.plate}
                        </span>
                        <button
                          type="button"
                          className="streetview-close"
                          onClick={closeStreetView}
                          aria-label="Cerrar street view"
                        >
                          ×
                        </button>
                      </div>
                      <div className="streetview-canvas" ref={streetViewContainerRef} />
                      {streetViewState !== 'ok' && (
                        <div className="streetview-status">
                          {streetViewState === 'loading'
                            ? 'Obteniendo panorama…'
                            : 'Sin cobertura Street View en estas coordenadas'}
                        </div>
                      )}
                      <div className="streetview-meta">
                        <span>
                          {streetViewTarget.location.lat.toFixed(5)},{' '}
                          {streetViewTarget.location.lng.toFixed(5)}
                        </span>
                        <span>{formatTime(streetViewTarget.timestamp)}</span>
                      </div>
                    </div>
                  )}
                  {!GOOGLE_MAPS_API_KEY && (
                    <div className="map-overlay">
                      <h3>Conecta Google Maps</h3>
                      <p>Agrega <span>REACT_APP_GOOGLE_MAPS_API_KEY</span> a tu entorno.</p>
                    </div>
                  )}
                  {apiBaseUrl && !authToken && (
                    <div className="map-overlay">
                      <h3>Inicia sesión para ver detecciones</h3>
                      <p>Ingresa tu token para habilitar la vista.</p>
                    </div>
                  )}
                  {mapError && (
                    <div className="map-overlay">
                      <h3>Mapa no disponible</h3>
                      <p>{mapError}</p>
                    </div>
                  )}
                </div>
                <div className="map-legend">
                  <span className="map-legend-item">
                    <span className="dot" style={{ background: 'var(--cat-orange)' }} /> Detección
                  </span>
                  <span className="map-legend-item">
                    <span className="dot" style={{ background: 'var(--cat-red)' }} /> Alerta
                  </span>
                  <span className="map-legend-item">
                    <span className="dot" style={{ background: 'var(--cat-cyan)' }} /> Ruta
                  </span>
                  <span className="map-legend-item">
                    <span className="dot" style={{ background: 'var(--cat-green)' }} /> Patrulla
                  </span>
                </div>
              </section>

              {/* detecciones (real AutoRescue feature) */}
              <aside className="side-panel">
                <div className="side-header">
                  <h2>Detecciones entrantes</h2>
                  <p>
                    {authToken ? visibleNotifications.length : 0}{' '}
                    {timeFilter === 'all'
                      ? 'alertas historicas'
                      : timeFilter === 'today'
                      ? 'alertas de hoy'
                      : timeFilter === '7d'
                      ? 'alertas de la ultima semana'
                      : 'alertas en las ultimas 24 horas'}
                  </p>
                  {apiBaseUrl && (
                    <div className="sync-meta">
                      <span>
                        {isFetching
                          ? 'Sincronizando notificaciones...'
                          : lastFetchAt
                          ? `Última sincronización ${formatTime(lastFetchAt)}`
                          : 'Sincronizacion pendiente'}
                      </span>
                      {fetchError && <span className="sync-error">{fetchError}</span>}
                    </div>
                  )}
                </div>
                <div className="filter-stack">
                  <div className="select-wrap">
                    <label htmlFor="plate-filter">Patentes detectadas</label>
                    <select
                      id="plate-filter"
                      value={selectedPlate}
                      onChange={(event) => setSelectedPlate(event.target.value)}
                    >
                      <option value="all">Todas las patentes</option>
                      {plateOptions.map((plate) => (
                        <option key={plate} value={plate}>
                          {plate}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="select-wrap">
                    <label htmlFor="time-filter">Rango de alertas</label>
                    <select
                      id="time-filter"
                      value={timeFilter}
                      onChange={(event) =>
                        setTimeFilter(event.target.value as 'all' | 'today' | '24h' | '7d')
                      }
                    >
                      <option value="24h">Ultimas 24 horas</option>
                      <option value="today">Solo hoy</option>
                      <option value="7d">Ultimos 7 dias</option>
                      <option value="all">Historico completo</option>
                    </select>
                  </div>
                </div>
                <div className="notification-list">
                  {visibleNotifications.map((notification) => (
                    <article
                      key={notification.id}
                      className={`notification-card ${
                        selectedPlate !== 'all' && notification.plate !== selectedPlate ? 'muted' : ''
                      } ${freshNotificationIds.has(notification.id) ? 'fresh' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <img
                        src={notification.image}
                        alt={`Patente ${notification.plate}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setActiveSighting(notification);
                          setShowSightingModal(true);
                        }}
                      />
                      <div>
                        <div className="card-header">
                          <span className="plate">{notification.plate}</span>
                          <span className="timestamp">
                            {formatTime(notification.timestamp)} · {formatDateLabel(notification.timestamp)}
                          </span>
                        </div>
                        <p className="meta">
                          Detectado por {getReporterName(notification)}
                          <span> | </span> {notification.location.lat.toFixed(4)},{' '}
                          {notification.location.lng.toFixed(4)}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
      {showAuthModal && !authToken && (
        <div className="modal-backdrop" role="presentation" onClick={() => setShowAuthModal(false)}>
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3 id="auth-modal-title">Acceso seguro</h3>
                <p>Ingresa tus credenciales para ver las detecciones.</p>
              </div>
              <button type="button" className="modal-close" onClick={() => setShowAuthModal(false)}>
                Cerrar
              </button>
            </div>
            <form
              className="auth-form"
              onSubmit={(event) => {
                event.preventDefault();
                handleLogin();
              }}
            >
              <label htmlFor="auth-email">Email</label>
              <input
                id="auth-email"
                type="email"
                autoComplete="email"
                value={authEmail}
                onChange={(event) => setAuthEmail(event.target.value)}
                placeholder="tu@email.com"
              />
              <label htmlFor="auth-password">Contrasena</label>
              <input
                id="auth-password"
                type="password"
                autoComplete="current-password"
                value={authPassword}
                onChange={(event) => setAuthPassword(event.target.value)}
                placeholder="Ingresa tu contrasena"
              />
              {authError && <span className="auth-error">{authError}</span>}
              <div className="auth-actions">
                <button type="button" className="ghost-button" onClick={() => setShowAuthModal(false)}>
                  Cancelar
                </button>
                <button type="submit" disabled={authLoading}>
                  {authLoading ? 'Ingresando...' : 'Ingresar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showSightingModal && activeSighting && (
        <div className="modal-backdrop" role="presentation" onClick={() => setShowSightingModal(false)}>
          <div
            className="modal-card modal-card--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sighting-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3 id="sighting-modal-title">Detalle de deteccion</h3>
                <p>Patente {activeSighting.plate}</p>
              </div>
              <button type="button" className="modal-close" onClick={() => setShowSightingModal(false)}>
                Cerrar
              </button>
            </div>
            <div className="sighting-modal-body">
              <img
                src={activeSighting.image}
                alt={`Patente ${activeSighting.plate}`}
                className="sighting-modal-image"
                onClick={() => setShowImageFullscreen(true)}
              />
              <div className="sighting-meta">
                <div>
                  <span className="label">Detectado por</span>
                  <strong>{getReporterName(activeSighting)}</strong>
                </div>
                <div>
                  <span className="label">Hora</span>
                  <strong>{formatTime(activeSighting.timestamp)}</strong>
                </div>
                <div>
                  <span className="label">Fecha</span>
                  <strong>{formatDateLabel(activeSighting.timestamp)}</strong>
                </div>
                <div>
                  <span className="label">Coordenadas</span>
                  <strong>
                    {activeSighting.location.lat.toFixed(4)}, {activeSighting.location.lng.toFixed(4)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showImageFullscreen && activeSighting && (
        <div
          className="fullscreen-backdrop"
          role="presentation"
          onClick={() => setShowImageFullscreen(false)}
        >
          <img
            src={activeSighting.image}
            alt={`Patente ${activeSighting.plate}`}
            className="fullscreen-image"
          />
        </div>
      )}
    </div>
  );
}

export default App;
